import { useContext, useState } from "react";
import { ISelectedFailure, ISummaryReportDashboardContextProvider } from "./SummaryReportDashboardContextInterfaces";
import { initialState } from "./SummaryReportDashboardContext";
import { SummaryReportDashboardContext } from "./SummaryReportDashboardContext";
import { ISummaryReportDashboardFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReportDashboard/components/SummaryReportDashboardFilters/SummaryReportDashboardFiltersInterfaces";
import {
    initialSummaryReportDashboardFilters,
    validMillSummaryDefaultFields,
} from "pages/Corporate/Locations/MillDashboards/SummaryReportDashboard/components/SummaryReportDashboardFilters/useSummaryReportDashboardFilters";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { UsersEntity } from "entities/Users/UsersEntity";
import { filterObjectProperties } from "utilities/utilities";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";

export const SummaryReportDashboardContextProvider: React.FC<ISummaryReportDashboardContextProvider> = ({
    children,
}) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [selectedProductionLine, setSelectedProductionLine] = useState(initialState.selectedProductionLine);
    const [productionLineName, setProductionLineName] = useState<string>("");
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filters, setFilters] = useState(initialState.filters);
    const [userFiltersDefault, setUserFiltersDefault] = useState<ISummaryReportDashboardFilters | null>(null);
    const [showAllProductionLines, setShowAllProductionLines] = useState<boolean>(true);
    const [selectedFailureType, setSelectedFailureType] = useState<ISelectedFailure | undefined>(undefined);
    const [selectedFailureCode, setSelectedFailureCode] = useState<ISelectedFailure | undefined>(undefined);
    const [selectedSecondaryFailureCode, setSelectedSecondaryFailureCode] = useState<ISelectedFailure | undefined>(
        undefined,
    );
    const [selectedMachineCenterByCode, setSelectedMachineCenterByCode] = useState<number | undefined>(undefined);
    const [totalDowntimeSeconds, setTotalDowntimeSeconds] = useState<number | undefined>(undefined);

    const [selectedDowntimeBar, setSelectedDowntimeBar] = useState<null | SVGGElement>(null);
    const [selectedDowntimeBarTitle, setSelectedDowntimeBarTitle] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault: ISummaryReportDashboardFilters = JSON.parse(userFilterPreference[0].filter_selection);
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.MILL_DASHBOARD_SUMMARY,
        currentUser?.user_id,
    );

    const handleSetTotalDowntimeSeconds = (value: number) => {
        setTotalDowntimeSeconds(value);
    };

    const handleShowFiltersChange = (value: boolean) => {
        setShowFilters(value);
    };

    const handleFiltersChange = (value: ISummaryReportDashboardFilters) => {
        setFilters(value);
    };

    const handleSelectedProductionLineChange = (value: number | string, productionLineName: string) => {
        if (showAllProductionLines) {
            setShowAllProductionLines(false);
        }
        setSelectedProductionLine(value);
        setProductionLineName(productionLineName);
        handleFiltersChange({
            productionLine: value,
            machineCenter: initialSummaryReportDashboardFilters.machineCenter,
            functionalLocation: initialSummaryReportDashboardFilters.functionalLocation,
            failureTypes: initialSummaryReportDashboardFilters.failureTypes,
            shift: initialSummaryReportDashboardFilters.shift,
        });
        setSelectedFailureType(undefined);
        setSelectedDowntimeBar(null);
        setSelectedMachineCenterByCode(undefined);
        setSelectedFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
    };

    const handleShowAllProductionLinesChange = (value: boolean) => {
        setShowAllProductionLines(value);
    };

    const handleSetSelectedFailureType = (value: ISelectedFailure | undefined) => {
        if (value?.id === selectedFailureType?.id) return;
        setSelectedFailureType(value);
        setSelectedDowntimeBar(null);
        setSelectedMachineCenterByCode(undefined);
        setSelectedFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
    };

    const handleSetSelectedFailureCode = (value: ISelectedFailure | undefined) => {
        if (value?.id === selectedFailureCode?.id) return;
        setSelectedFailureCode(value);
        setSelectedDowntimeBar(null);
        setSelectedMachineCenterByCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
    };

    const handleSetSelectedSecondaryFailureCode = (value: ISelectedFailure | undefined) => {
        setSelectedSecondaryFailureCode(value);
    };

    const handleSetSelectedMachineCenterByCode = (value: number | undefined) => {
        setSelectedMachineCenterByCode(value);
    };

    const resetSelections = () => {
        setSelectedFailureType(undefined);
        setSelectedFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
        setSelectedMachineCenterByCode(undefined);
        setSelectedDowntimeBar(null);
    };

    const handleSetSelectedDowntimeBar = (value: null | SVGGElement) => {
        setSelectedDowntimeBar(value);
    };

    const handleSelectedDowntimeBarTitle = (value: string | null) => {
        setSelectedDowntimeBarTitle(value);
    };

    const handleSetFiltersDefault = async (summaryFilters: ISummaryReportDashboardFilters) => {
        try {
            const validFilters = filterObjectProperties(summaryFilters, validMillSummaryDefaultFields);
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.MILL_DASHBOARD_SUMMARY,
                        filter_selection: JSON.stringify(validFilters),
                    },
                },
            });
            await refetch();
            resetSelections();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.MILL_DASHBOARD_SUMMARY,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const providerValue = {
        selectedProductionLine,
        selectedProductionLineName: productionLineName,
        filters,
        showFilters,
        totalDowntimeSeconds,
        handleSetTotalDowntimeSeconds,
        handleFiltersChange,
        handleSelectedProductionLineChange,
        handleShowFiltersChange,
        showAllProductionLines,
        handleShowAllProductionLinesChange,
        handleSetSelectedFailureType,
        selectedFailureType,
        resetSelections,
        handleSetSelectedFailureCode,
        selectedFailureCode,
        handleSetSelectedSecondaryFailureCode,
        selectedSecondaryFailureCode,
        handleSetSelectedDowntimeBar,
        selectedDowntimeBar,
        handleSelectedDowntimeBarTitle,
        selectedDowntimeBarTitle,
        handleSetSelectedMachineCenterByCode,
        selectedMachineCenterByCode,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
    };
    return (
        <SummaryReportDashboardContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </SummaryReportDashboardContext.Provider>
    );
};
